// Version 4 (29.10.2026): Katalog „Produkte und Anlagen“ nach der Struktur von BuyParto.
// Drei Gruppen: Komponenten und Automation, Maschinen und Anlagen, Hersteller. Jede Kategorie und jeder Hersteller hat eine eigene Seite.
// Texte: catalogCopy.js. Baureihen: catalogSeries.json. Version 1 bis 3 nutzen weiter src/v3/content/products.js.
import { KOMPONENTEN, MASCHINEN, HERSTELLER, BRAND_TEXT, COMMON, HERSTELLER_PAGE } from "./catalogCopy";
import SERIES from "./catalogSeries.json";
import SEO_TEXT from "./catalogSeo.json";
import MASK_KEB from "@/v4/assets/logo-masken/keb.png";
import MASK_IFM from "@/v4/assets/logo-masken/ifm.png";
import MASK_HEIDENHAIN from "@/v4/assets/logo-masken/heidenhain.png";
import MASK_PF from "@/v4/assets/logo-masken/pepperl-fuchs.png";

export const BASE = "/produkte-und-anlagen";
export const GROUP_PATH = {
    komponenten: `${BASE}/komponenten-automation`,
    maschinen: `${BASE}/maschinen-anlagen`,
    hersteller: `${BASE}/hersteller`,
};

const series = (key) => (key && SERIES[key]) || [];
const BRAND_BY_ID = Object.fromEntries(HERSTELLER.map((h) => [h.id, h]));
const CAT_BY_ID = Object.fromEntries(KOMPONENTEN.map((c) => [c.id, c]));

// Inhalte je Seite im Format der Vorlagen (h1, sub, sections …) plus neue Felder (intro, more, series, brands)
const catContent = (group, c, i) => ({
    ...c,
    group,
    path: `${GROUP_PATH[group]}/${c.id}`,
    text: c.sub,
    more: c.more || COMMON[group][i % COMMON[group].length],
    series: series(c.bp),
    brands: (c.brands || []).filter((b) => BRAND_BY_ID[b]),
    seoText: SEO_TEXT[c.id] || null,
});

const brandContent = (h, i) => {
    const s = series(h.id);
    const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const names = [h.de, h.de.split(" ")[0], h.de.replace(/-/g, " ")].map(esc).join("|");
    const strip = (t) => t.replace(new RegExp(`^(Komplette )?(${names})( Electric| Eurodrive)?\\s*`, "i"), "");
    // Ein Baureihenbegriff im Satz; Adjektiv am Anfang klein (z. B. „elektrische Antriebssysteme“)
    const lowAdj = (t) => t.replace(/^([A-ZÄÖÜ][a-zäöüß]*(?:isch|ell|iv|lich|geschützt)(?:e|en|er|es))\b/, (w) => w.toLowerCase());
    const a = lowAdj(s[0] ? strip(s[0].t) : "Steuerungstechnik");
    // Teaser der Herstellerkarte: Produktnamen aus den Baureihen (etwa „S7-1200, SINAMICS G120“), sonst die Bereiche
    const isName = (t) => /[A-Za-z]/.test(t) && /[0-9]|[A-Z]{2,}|[a-z][A-Z]/.test(t) && !/Module|^(I\/O|CPU|HMI|SPS|RFID|KI)\b/.test(t);
    const names3 = s.slice(0, 3).flatMap((x) => x.d.split(",").map((t) => t.trim()).filter(isName).slice(0, 2)).slice(0, 4);
    const cat = CAT_BY_ID[h.cat];
    const cats = KOMPONENTEN.filter((c) => (c.brands || []).includes(h.id)).map((c) => c.id);
    return {
        id: h.id,
        de: h.de,
        group: "hersteller",
        path: `${GROUP_PATH.hersteller}/${h.id}`,
        h1: `${h.de} verkaufen`,
        seo: `${h.de} verkaufen, ${h.de} Ankauf`,
        sub: BRAND_TEXT.sub(h.de, a),
        text: BRAND_TEXT.sub(h.de, a),
        intro: BRAND_TEXT.intro[i % BRAND_TEXT.intro.length](h.de),
        more: COMMON.komponenten[(i + 1) % COMMON.komponenten.length],
        series: s,
        short: names3.length >= 2 ? names3.join(", ") : s.slice(0, 3).map((x, j) => (j ? lowAdj(strip(x.t)) : strip(x.t))).filter(Boolean).join(", "),
        cats: cats.length ? cats : [h.cat],
        img: cat?.img || "/media/parto/artikel-altbestaende.jpg",
        alt: "",
        seoText: SEO_TEXT[h.id] || null,
    };
};

export const CATALOG = {
    komponenten: KOMPONENTEN.map((c, i) => catContent("komponenten", c, i)),
    maschinen: MASCHINEN.map((c, i) => catContent("maschinen", c, i)),
    hersteller: HERSTELLER.map((h, i) => brandContent(h, i)),
};

export const ALL_ENTRIES = [...CATALOG.komponenten, ...CATALOG.maschinen, ...CATALOG.hersteller];
export const entryByPath = (path) => ALL_ENTRIES.find((e) => e.path === path) || null;
export const brandById = (id) => CATALOG.hersteller.find((h) => h.id === id) || null;
export const catById = (id) => [...CATALOG.komponenten, ...CATALOG.maschinen].find((c) => c.id === id) || null;

// Gruppenseiten (Vorlage Products): Inhalte wie bisher plus Abschnitte aus dem Katalog
const sectionsOf = (list) => Object.fromEntries(list.map((c) => [c.id, { text: c.sub, items: c.items, img: c.img, alt: c.alt }]));
export const GROUP_CONTENT = {
    [GROUP_PATH.komponenten]: {
        h1: "Komponenten und Automation",
        sub: "SPS, Frequenzumrichter, Servomotoren, Robotik, Sensoren oder Pneumatik: Parto übernimmt Ihre Automatisierungskomponenten und bewertet sie je Position.",
        seo: "Industriekomponenten und Automatisierungstechnik verkaufen",
        landing: "/bestaende-verkaufen",
        landingLabel: "Bestand anbieten",
        condition: "Neu, originalverpackt oder gebraucht",
        seoText: SEO_TEXT["gruppe-komponenten"] || null,
        sections: sectionsOf(CATALOG.komponenten),
    },
    [GROUP_PATH.maschinen]: {
        h1: "Maschinen und Anlagen",
        sub: "Von der Einzelmaschine bis zur kompletten Produktionslinie: Parto bewertet und organisiert Abbau und Abtransport.",
        seo: "Maschinen und Anlagen verkaufen, Gebrauchtmaschinen Ankauf",
        landing: "/maschinen-anlagen-verkaufen",
        landingLabel: "Maschinen anbieten",
        condition: "Laufend, stillgelegt oder defekt",
        seoText: SEO_TEXT["gruppe-maschinen"] || null,
        sections: sectionsOf(CATALOG.maschinen),
    },
    [GROUP_PATH.hersteller]: {
        h1: HERSTELLER_PAGE.h1,
        sub: HERSTELLER_PAGE.sub,
        seo: HERSTELLER_PAGE.seo,
        text: HERSTELLER_PAGE.text,
        benefits: HERSTELLER_PAGE.benefits,
        landing: "/bestaende-verkaufen",
        landingLabel: "Bestand anbieten",
        condition: "Alle gängigen Hersteller",
        seoText: SEO_TEXT["gruppe-hersteller"] || null,
        sections: Object.fromEntries(CATALOG.hersteller.map((h) => [h.id, { text: h.sub, img: h.img, alt: "" }])),
    },
};

// Auswahl „Kategorien“ im Formular (Version 4): alle Komponenten und Maschinenkategorien des Katalogs
export const CATEGORY_OPTIONS = [...CATALOG.komponenten, ...CATALOG.maschinen].map((c) => ({ id: c.id, label: c.de }));

// Lange Wörter in Überschriften: weiche Trennstelle (nur sichtbar, wenn das Wort auf dem Handy nicht in die Zeile passt)
const SHY = ["Automatisierungs|technik", "Holzbearbeitungs|maschinen", "Kunststoff|maschinen", "Maschinen|ersatzteile", "Werkzeug|maschinen", "Produktions|anlagen", "Industrie|komponenten", "Industrie|elektronik", "Leistungs|technik", "Steuerungs|technik", "Antriebs|technik", "Pneumatik|komponenten"];
export const shy = (t) => SHY.reduce((s, w) => s.split(w.replace("|", "")).join(w.replace("|", "­")), String(t || ""));

// Herstellerlogos (vorhandene Dateien in public/media/parto/hersteller, Name = Hersteller ID)
export const brandLogo = (id) => `/media/parto/hersteller/${id}.png`;
// Seitenverhältnis je Logo und einfarbige Maske (Logos mit weißen Innenflächen: eigene kleine Maske aus src/v4/assets/logo-masken, wird in den Code eingebettet)
const LOGO_RATIO = {"abb": 2.613, "allen-bradley": 4.486, "b-r": 1.51, "baumueller": 3.333, "beckhoff": 5.287, "bosch-rexroth": 2.497, "control-techniques": 2.427, "danfoss": 2.559, "eaton": 3.727, "fanuc": 5.896, "festo": 5.556, "heidenhain": 5.319, "honeywell": 5.647, "ifm": 1.0, "indramat": 2.937, "keb": 2.217, "keyence": 5.5, "kuka": 5.81, "lenze": 3.212, "mitsubishi": 6.12, "omron": 4.933, "pepperl-fuchs": 8.402, "phoenix-contact": 4.034, "schneider-electric": 3.382, "sew-eurodrive": 2.148, "sick": 3.141, "siemens": 6.213, "yaskawa": 6.573};
const LOGO_MONO = { keb: MASK_KEB, ifm: MASK_IFM, heidenhain: MASK_HEIDENHAIN, "pepperl-fuchs": MASK_PF };
export const brandLogoMeta = (id) => {
    const ratio = LOGO_RATIO[id] || 3;
    return {
        ratio,
        h: ratio < 2 ? 2.6 : ratio > 5.5 ? 1.6 : ratio > 4.4 ? 1.8 : 2.1,
        mask: LOGO_MONO[id] || brandLogo(id),
        color: brandLogo(id),
    };
};
export const BRAND_NOTE = "Die Herstellernamen und Logos dienen nur der Orientierung. Es besteht keine Verbindung zu den Herstellern, alle Marken gehören ihren jeweiligen Inhabern.";
